import React from 'react';
import { IonItem, IonBadge, IonButton, IonIcon, IonThumbnail } from '@ionic/react';
import { cashOutline, closeCircleOutline, imageOutline, addCircleOutline, swapHorizontalOutline } from 'ionicons/icons';
import { Card } from '../types';
import { API_BASE } from '../config/api';

const resolveImageUrl = (url?: string) =>
  url && url.startsWith('/uploads') ? `${API_BASE}${url}` : url;

interface Props {
  card: Card;
  onClick?: () => void;
  onSale?: (card: Card) => void;
  onLost?: (card: Card) => void;
  onAddQuantity?: (card: Card) => void;
  onTradeOut?: (card: Card) => void;
}

const CardItem: React.FC<Props> = ({ card, onClick, onSale, onLost, onAddQuantity, onTradeOut }) => {
  const imgSrc = resolveImageUrl(card.image_front_url);

  return (
    <IonItem button onClick={onClick} detail={false} style={{ '--padding-start': '12px', '--inner-padding-end': '12px' }}>
      <IonThumbnail slot="start" style={{ '--size': '56px', borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--ion-color-light)', marginRight: 12 }}>
        {imgSrc ? (
          <img src={imgSrc} alt={card.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <IonIcon icon={imageOutline} style={{ fontSize: 24, color: 'var(--ion-color-medium)' }} />
        )}
      </IonThumbnail>
      <div style={{ flex: 1, minWidth: 0, padding: '6px 0' }}>
        {/* Row 1: Card Name (left) | Binder Name (right) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: 1 }}>
            {card.name}
          </div>
          {card.binder_name && (
            <span style={{ fontSize: 11, color: 'var(--ion-color-primary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {card.binder_name}
            </span>
          )}
        </div>

        {/* Row 2: Game/Set/Number (left) | Action buttons (right) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 }}>
          <div style={{ fontSize: 13, color: 'var(--ion-color-medium)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {card.game?.name || 'Unknown Game'}
            {card.set_name && ` \u00B7 ${card.set_name}`}
            {card.card_number && ` #${card.card_number}`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0, marginLeft: 8 }}>
            {onAddQuantity && (
              <IonButton
                fill="clear"
                size="small"
                color="primary"
                style={{ '--padding-start': '4px', '--padding-end': '4px' }}
                onClick={(e) => { e.stopPropagation(); onAddQuantity(card); }}
              >
                <IonIcon slot="icon-only" icon={addCircleOutline} style={{ fontSize: 18 }} />
              </IonButton>
            )}
            {onTradeOut && (
              <IonButton
                fill="clear"
                size="small"
                color="warning"
                style={{ '--padding-start': '4px', '--padding-end': '4px' }}
                onClick={(e) => { e.stopPropagation(); onTradeOut(card); }}
              >
                <IonIcon slot="icon-only" icon={swapHorizontalOutline} style={{ fontSize: 18 }} />
              </IonButton>
            )}
            {onSale && (
              <IonButton
                fill="clear"
                size="small"
                color="success"
                style={{ '--padding-start': '4px', '--padding-end': '4px' }}
                onClick={(e) => { e.stopPropagation(); onSale(card); }}
              >
                <IonIcon slot="icon-only" icon={cashOutline} style={{ fontSize: 18 }} />
              </IonButton>
            )}
            {onLost && (
              <IonButton
                fill="clear"
                size="small"
                color="danger"
                style={{ '--padding-start': '4px', '--padding-end': '4px' }}
                onClick={(e) => { e.stopPropagation(); onLost(card); }}
              >
                <IonIcon slot="icon-only" icon={closeCircleOutline} style={{ fontSize: 18 }} />
              </IonButton>
            )}
          </div>
        </div>

        {/* Row 3: Condition/Rarity badges (left) | Qty & Value (right) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {card.condition && <IonBadge color="medium" style={{ fontSize: 11 }}>{card.condition}</IonBadge>}
            {card.rarity && <IonBadge color="tertiary" style={{ fontSize: 11 }}>{card.rarity}</IonBadge>}
            {card.is_foil && <IonBadge color="warning" style={{ fontSize: 11 }}>Foil</IonBadge>}
          </div>
          <span style={{ fontSize: 12, color: 'var(--ion-color-medium)', flexShrink: 0, marginLeft: 8 }}>
            x{card.quantity}
            {card.estimated_value != null && ` \u00B7 $${card.estimated_value.toFixed(2)}`}
          </span>
        </div>
      </div>
    </IonItem>
  );
};

export default CardItem;
